import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../apis/EdisonApi";
import { toast } from "react-toastify";
import ReactECharts from "echarts-for-react";
import energyPalette from "../../theme/energyPalette";

export const DAY_HOURS = Array.from(
    { length: 24 },
    (_, hour) => `${hour.toString().padStart(2, "0")}:00`
);

export const padHourlySeries = (values = []) =>
    Array.from(
        { length: DAY_HOURS.length },
        (_, hour) => (Array.isArray(values) && hour < values.length ? values[hour] : null)
    );

export const withOuterStackRadius = (stack, seriesIndex, radius = 4) =>
    stack[seriesIndex].map((value, dataIndex) => {
        const visibleSeries = stack
            .map((series, index) => ({ index, value: Number(series[dataIndex]) || 0 }))
            .filter(({ value: stackValue }) => stackValue !== 0)
            .map(({ index }) => index);
        const isTop = visibleSeries[visibleSeries.length - 1] === seriesIndex;

        return {
            value,
            itemStyle: {
                borderRadius: [
                    isTop ? radius : 0,
                    isTop ? radius : 0,
                    0,
                    0,
                ],
            },
        };
    });

export const formatEnergyTooltip = (params = []) => {
    const entries = Array.isArray(params) ? params : [params];
    if (entries.length === 0) return "";

    const axisLabel = entries[0].axisValueLabel || entries[0].axisValue || "";
    const values = entries.map((entry) => {
        const rawValue = entry.data && typeof entry.data === "object"
            ? entry.data.value
            : entry.value;
        const hasValue = rawValue !== null && rawValue !== undefined && rawValue !== "";
        const numericValue = hasValue ? Number(rawValue) : Number.NaN;
        const displayValue = Number.isFinite(numericValue) ? numericValue.toFixed(3) : "—";

        return `${entry.marker || ""}${entry.seriesName}: <strong>${displayValue} kWh</strong>`;
    });

    return [axisLabel, ...values].filter(Boolean).join("<br/>");
};

const HomeCharts = () => {
    const [status, setStatus] = useState('NA');
    const [chartDate, setChartDate] = useState(new Date());
    const [loading, setLoading] = useState(false);

    const getChartInterval = useCallback(async (signal) => {
        try {
            setLoading(true);
            setStatus('NA');
            const resp = await api.get("/home/charts-interval", {
                params: { date: chartDate.toISOString() },
                signal,
            });
            setStatus(resp.data);
        } catch(err) {
            if (err.name === "CanceledError") return;

            const statusCode = err.response?.status;
            if ([400, 404, 500].includes(statusCode)) {
                toast.error(`API Error: ${statusCode}. ${err.response?.data || "Unable to load chart data."}`);
            } else {
                toast.error("API Error getting consumption data, valid dates start from September 15 2025");
            }
        } finally {
            setLoading(false);
        }
    }, [chartDate]);

    const getTodayPowerStats = useCallback(async (signal) => {
        try {
            setLoading(true);
            setStatus('NA');
            const resp = await api.get("/home/aggregate-energy-data", { signal });
            setStatus(resp.data);
        } catch(err) {
            if (err.name === "CanceledError") return;
            toast.error('API Error getting aggregated power data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const today = new Date();
        const isToday = chartDate.toDateString() === today.toDateString();
        const controller = new AbortController();

        if (isToday) {
            getTodayPowerStats(controller.signal);
        } else {
            getChartInterval(controller.signal);
        }

        return () => controller.abort();
    }, [chartDate, getChartInterval, getTodayPowerStats]);

    const getConsumptionChartOption = () => {
        
        if (status === 'NA') {
            return;
        }

        const consumptionStack = [
            padHourlySeries(status.ePanelD),
            padHourlySeries(status.eBattD),
            padHourlySeries(status.eGridD),
        ];
        const consumption = padHourlySeries(status.eConsumD);


        return {
            animation: false,
            tooltip: {
                trigger: 'axis',
                formatter: formatEnergyTooltip,
                axisPointer: {            // Use axis to trigger tooltip
                    type: 'shadow'        // 'shadow' as default; can also be 'line' or 'none'
                }
            },
            title: {
                text: 'Consumption sources',
                left: '6px'
            },
            legend: {
                type: 'scroll',
                selectedMode: false,
                data: ['Solar production', 'Battery', 'Grid import', 'Consumption'],
                bottom: 0,
                itemWidth: 14,
                itemHeight: 8,
                itemGap: 18,
                textStyle: { color: '#526159', fontSize: 11 }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                show: true,        
                data: DAY_HOURS
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: '{value} kWh'
                },
            },
            toolbox: {
                show: true,
                orient: 'vertical',
                left: 'right',
                top: 'center',
                feature: {
                    dataView: { readOnly: false },
                    magicType: { type: ["bar", "stack", "tiled"] },
                    restore: {},
                    saveAsImage: {},
                },
                /*
                feature: {
                    mark: {show: true},
                    dataView: {show: true, readOnly: false},
                    magicType: {show: true, type: ['line', 'bar', 'stack', 'tiled']},
                    restore: {show: true},
                    saveAsImage: {show: true}
                }*/
            },
            series: [
                {
                    name: 'Solar production',
                    type: 'bar',
                    stack: 'total',
                    itemStyle:{
                        color: energyPalette.solar
                    },
                    label: {
                        show: false
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [320, 302, 301, 334, 390, 330]
                    data: withOuterStackRadius(consumptionStack, 0)
                },
                {
                    name: 'Battery',
                    type: 'bar',
                    stack: 'total',
                    itemStyle:{
                        color: energyPalette.battery
                    },
                    label: {
                        show: false
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [120, 132, 157, 134, 90, 230]
                    data: withOuterStackRadius(consumptionStack, 1)
                },
                {
                    name: 'Grid import',
                    type: 'bar',
                    stack: 'total',
                    itemStyle:{
                        color: energyPalette.grid
                    },
                    label: {
                        show: false
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [220, 182, 90, 234, 160, 200]
                    // TODO add grid diff!
                    data: withOuterStackRadius(consumptionStack, 2)
                },
                {
                    name: 'Consumption',
                    type: 'bar',
                    // stack: 'total',
                    itemStyle:{
                        color: energyPalette.consumption,
                        borderRadius: [4, 4, 0, 0]
                    },
                    label: {
                        show: false
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [220, 182, 90, 234, 160, 200]
                    // TODO add grid diff!
                    data: consumption
                }
            ]
        };
    };

    const getGreenChartOption = () => {
        if (status === 'NA') {
            return;
        }

        const production = padHourlySeries(status.eProd);
        const consumption = padHourlySeries(status.eConsumD);
        const gridImport = padHourlySeries(status.eBought);
        const gridExport = padHourlySeries(status.eSoldD);

        return {
            animation: false,
            tooltip: {
                trigger: 'axis',
                formatter: formatEnergyTooltip,
                axisPointer: {
                    type: 'shadow'
                }
            },
            title: {
                text: 'Energy balance',
                left: '6px'
            },
            legend: {
                type: 'scroll',
                selectedMode: false,
                data: ['Solar production', 'Consumption', 'Grid import', 'Grid export'],
                bottom: 0,
                itemWidth: 14,
                itemHeight: 8,
                itemGap: 18,
                textStyle: { color: '#526159', fontSize: 11 }
            },
            toolbox: {
                show: true,
                orient: 'vertical',
                left: 'right',
                top: 'center',
                feature: {
                    dataZoom: {},
                    dataView: { readOnly: false },
                    magicType: { type: ["bar", "stack", "tiled"] },
                    restore: {},
                    saveAsImage: {},
                },
                /*
                feature: {
                    mark: {show: true},
                    dataView: {show: true, readOnly: false},
                    magicType: {show: true, type: ['line', 'bar', 'stack', 'tiled']},
                    restore: {show: true},
                    saveAsImage: {show: true}
                }*/
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: [
                {
                    type: 'category',
                    axisTick: {show: false},
                    show: true,
                    data: DAY_HOURS
                }
            ],
            yAxis: [
                {
                    type: 'value',
                    axisLabel: {
                        formatter: '{value} kWh'
                    },
                    rich: {
                        nameTextStyle: {
                            name: "kWh",
                            verticalAlign: "middle"
                        }
                    }
                }
            ],
            series: [
                {
                    name: 'Solar production',
                    type: 'bar',            
                    barGap: 0,
                    label: {
                        show: false
                    },
                    itemStyle:{
                        color: energyPalette.solar,
                        borderRadius: [4, 4, 0, 0]
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [100, 120, 85, 170, 112, 102]
                    data: production
                },
                {
                    name: 'Consumption',
                    type: 'bar',
                    itemStyle:{
                        color: energyPalette.consumption,
                        borderRadius: [4, 4, 0, 0]
                    },
                    label: {
                        show: false
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [120, 132, 102, 160, 120, 140]
                    data: consumption
                },
                {
                    name: 'Grid import',
                    type: 'bar',
                    // stack: 'total',
                    itemStyle:{
                        color: energyPalette.grid,
                        borderRadius: [4, 4, 0, 0]
                    },
                    label: {
                        show: false
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [220, 182, 90, 234, 160, 200]
                    // TODO add grid diff!
                    data: gridImport
                },
                {
                    name: 'Grid export',
                    type: 'bar',
                    // stack: 'total',
                    itemStyle:{
                        color: energyPalette.export,
                        borderRadius: [4, 4, 0, 0]
                    },
                    label: {
                        show: false
                    },
                    emphasis: {
                        disabled: true
                    },
                    // data: [220, 182, 90, 234, 160, 200]
                    // TODO add grid diff!
                    data: gridExport
                }
            ]
        };
    };

    const renderConsumptionChart = () => {
        if (status === 'NA') return null;

        return (
            <ReactECharts
                option={getConsumptionChartOption()}
                notMerge={true}
                lazyUpdate={true}
            />
        );
    };

    const renderGreenChart = () => {
        if (status === 'NA') return null;

        return (
            <ReactECharts
                option={getGreenChartOption()}
                notMerge={true}
                lazyUpdate={true}
            />
        );
    };

	return (
		<section className="energy-analytics-panel" aria-labelledby="energy-analytics-title">
			<header className="energy-analytics-panel__header">
				<div>
					<h2 id="energy-analytics-title">Energy analytics</h2>
					<p>The selected date applies to both charts.</p>
				</div>
				<div className="chart-date-control">
					<label htmlFor="chart-date">Date</label>
					<DatePicker
						id="chart-date"
						selected={chartDate}
						onChange={(date) => date && setChartDate(date)}
						maxDate={new Date()}
						dateFormat="dd/MM/yyyy"
					/>
				</div>
			</header>

			{status === "NA" ? (
				<div className="energy-analytics-panel__loading" role="status" aria-live="polite">
					{loading ? "Loading energy data…" : "Energy data unavailable."}
				</div>
			) : (
				<React.Fragment>
					<div id="cons-chart" className="home-charts">
						{renderConsumptionChart()}
					</div>
					<div id="cons-prod-chart" className="home-charts">
						{renderGreenChart()}
					</div>
				</React.Fragment>
			)}
		</section>
	);
};

export default HomeCharts;
